import {enableProdMode, APP_INITIALIZER, ErrorHandler, importProvidersFrom} from '@angular/core';
import {environment} from '@ccp/environments/global/environment';
import {UserAgent} from '@ccp/app/lib/user-agent';
import {NotificationService} from '@ccp/services/notifications/notification.service';
import {DigitalCertificateService} from '@ccp/services/dc/digital-certificate.service';
import {RouterTrackerService} from '@ccp/services/router-tracker/router-tracker.service';
import {WarrantyService} from '@ccp/services/warranty/warranty.service';
import {StorageContextService} from '@ccp/services/storage-context/storage-context.service';
import {UserService} from '@ccp/services/user/user.service';
import {AnalyticsService} from '@ccp/services/analytics/analytics.service';
import {GlobalErrorHandler} from '@ccp/app/errors-handler/global-error-handler';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {HttpRequestInterceptor} from '@ccp/app/http-interceptors/http-request.interceptor';
import {provideAnimations} from '@angular/platform-browser/animations';
import {BrowserModule, bootstrapApplication} from '@angular/platform-browser';
import {FormsModule} from '@angular/forms';
import {YouTubePlayerModule} from '@angular/youtube-player';
import {AppComponent} from './app/app.component';
import {InitializationService} from '@ccp/services/app-context/initialization.service';
import {LocaleContextService} from '@ccp/services/app-context/locale-context.service';
import {UserContextService} from '@ccp/services/app-context/user-context.service';
import {ProductContextService} from '@ccp/services/app-context/product-context.service';
import {PageLayoutService} from '@ccp/services/page-layout/page-layout.service';
import {CultureService} from '@ccp/services/culture/culture.service';
import {TransliterationService} from '@ccp/services/transliteration/transliteration.service';
import {ScriptInjectionService} from '@ccp/services/utils/script-injection.service';
import {WechatService} from '@ccp/services/wechat/wechat.service';
import {LocalizationService} from '@ccp/services/localization/localization.service';
import {PortfolioService} from '@ccp/services/portfolio/portfolio.service';
import {provideRouter, withComponentInputBinding, withDisabledInitialNavigation, withPreloading, withRouterConfig} from '@angular/router';
import {RoutesDictionary} from '@ccp/app/routes-dictionary';
import {SelectivePreloadStrategy} from '@ccp/services/preload-strategy/selective-preload-strategy.service';

if (environment.production) {
  enableProdMode();
}

/**
 * CCP-4185
 * On Cartier, on MacOs only, there is a bug displaying the font icon.
 * We need to target this platform as fast as possible before the page loads.
 */
if (UserAgent.isOS(UserAgent.OS.MAC)) {
  document.body.classList.add('os-mac');
}

export function initializeApp(initService: InitializationService): () => Promise<void> {
  return () => initService.initialize();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(BrowserModule, FormsModule, YouTubePlayerModule),
    provideRouter(
      RoutesDictionary,
      withPreloading(SelectivePreloadStrategy),
      withDisabledInitialNavigation(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withComponentInputBinding(),
    ),

    LocaleContextService,
    UserContextService,
    ProductContextService,
    InitializationService,

    PageLayoutService,
    CultureService,
    WechatService,
    ScriptInjectionService,
    TransliterationService,

    LocalizationService,
    NotificationService,
    DigitalCertificateService,
    PageLayoutService,
    PortfolioService,
    RouterTrackerService,
    WarrantyService,
    StorageContextService,
    UserService,
    WechatService,
    AnalyticsService,

    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [InitializationService],
      multi: true,
    },

    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      deps: [LocaleContextService, UserContextService],
      multi: true,
    },

    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch((err) => console.error('Bootstrap failed:', err));
